import { useCallback } from 'react';

import { PregnancyCompletionType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { assertUnreachable } from '@dreryk/react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';

import { patientMedicalInformationTranslationKeys } from '../../translations';
import { refetchAllMedicalInformation } from '../shared/refetchAllMedicalInformation';

import {
   GynecologicalInterviewFormController,
   GynecologicalInterviewPatientPregnantFormController,
   GynecologicalInterviewPregnancyCompletionFormController,
} from './gynecologicalInterviewAddEntryForm/gynecologicalInterviewAddEntryForm.types';
import { GynecologicalInterviewPatientPregnantCreateAction } from './GynecologicalInterviewPatientPregnantCreateAction';
import { GynecologicalInterviewPregnancyCompletionAction } from './GynecologicalInterviewPregnancyCompletionAction';
import { GynecologicalInterviewRegularInterviewCreateAction } from './GynecologicalInterviewRegularInterviewCreateAction';

type GynecologicalInterviewActionRendererForPatientPregnant = {
   interviewType: 'PATIENT_PREGNANT';
   handleSubmit: GynecologicalInterviewPatientPregnantFormController['handleSubmit'];
};
type GynecologicalInterviewActionRendererForPregnancyCompletion = {
   interviewType: PregnancyCompletionType;
   handleSubmit: GynecologicalInterviewPregnancyCompletionFormController['handleSubmit'];
};
type GynecologicalInterviewActionRendererForRegularInterview = {
   interviewType: 'REGULAR_INTERVIEW' | null;
   handleSubmit: GynecologicalInterviewFormController['handleSubmit'];
};

type GynecologicalInterviewActionsRendererProps = {
   readonly medicalInformationId: number;
   readonly patientId: number;
   readonly onSuccess: VoidFunction;
} & (
   | GynecologicalInterviewActionRendererForPatientPregnant
   | GynecologicalInterviewActionRendererForPregnancyCompletion
   | GynecologicalInterviewActionRendererForRegularInterview
);

export const GynecologicalInterviewActionsRenderer = (
   props: GynecologicalInterviewActionsRendererProps
): JSX.Element => {
   const { interviewType } = props;
   const handleAddEntrySuccess = useCallback(() => {
      props.onSuccess();
      dispatchSnackbarPositiveEvent(patientMedicalInformationTranslationKeys.successfullyAddedNewEntry);
      refetchAllMedicalInformation();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onSuccess]);
   switch (interviewType) {
      case 'PATIENT_PREGNANT':
         return (
            <>
               <GynecologicalInterviewPatientPregnantCreateAction
                  handleSubmit={props.handleSubmit}
                  medicalInformationId={props.medicalInformationId}
                  patientId={props.patientId}
                  onSuccess={handleAddEntrySuccess}
                  registerMedicalEvent
               />
               <GynecologicalInterviewPatientPregnantCreateAction
                  handleSubmit={props.handleSubmit}
                  medicalInformationId={props.medicalInformationId}
                  patientId={props.patientId}
                  onSuccess={handleAddEntrySuccess}
                  registerMedicalEvent={false}
               />
            </>
         );
      case 'DELIVERY':
      case 'DEAD_DELIVERY':
      case 'MISCARRIAGE':
      case 'OTHER':
         return (
            <>
               <GynecologicalInterviewPregnancyCompletionAction
                  handleSubmit={props.handleSubmit}
                  medicalInformationId={props.medicalInformationId}
                  pregnancyCompletionType={interviewType}
                  patientId={props.patientId}
                  onSuccess={handleAddEntrySuccess}
                  registerMedicalEvent
               />
               <GynecologicalInterviewPregnancyCompletionAction
                  handleSubmit={props.handleSubmit}
                  medicalInformationId={props.medicalInformationId}
                  pregnancyCompletionType={interviewType}
                  patientId={props.patientId}
                  onSuccess={handleAddEntrySuccess}
                  registerMedicalEvent={false}
               />
            </>
         );
      case 'REGULAR_INTERVIEW':
      case null:
         return (
            <GynecologicalInterviewRegularInterviewCreateAction
               handleSubmit={props.handleSubmit}
               medicalInformationId={props.medicalInformationId}
               patientId={props.patientId}
               onSuccess={handleAddEntrySuccess}
            />
         );
      default:
         return assertUnreachable(interviewType);
   }
};
